import { Delete, Edit, Close, Error, Info } from "@mui/icons-material";
import { Autocomplete, Box, TextField, Tooltip } from "@mui/material";
import { useState, useEffect } from "react";
import { useLoaderData, useRevalidator } from "react-router-dom";

import { useQueryParams } from "../../../../../hooks/useQueryParams";
import useService from "../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import Colors from "../../../../global/Colors";
import FormatISODate from "../../../../global/FormatISODate";
import LoadingIndicator from "../../../../global/LoadingIndicator";
import ModalButton from "../../../../global/ModalButton";
import ModalShell from "../../../../global/ModalShell";
import ResponseModal from "../../../../global/ResponseModal";
import AssociateAppointments from "../../../../navigations/documents/AssociateAppointments";
import CustomIconButton from "../../../../ui library/components/buttons/CustomIconButton";
import CustomDataGrid from "../../../../ui library/components/data-grid/CustomDataGrid";
import Pagination from "../../../../ui library/components/data-grid/Pagination";
import { getExtension } from "../../../../ui library/helpers/functions";
import useHiddenDocumentTypeKeys from "../../hooks/useHiddenDocumentTypeKeys";
import { getSelectableDocTypes } from "../../utils/documentTypeOptions";
import { useDocumentListStore } from "../hooks/useDocumentListStore";
import STRINGS from "../utils/strings";

import ApprovalChecklist from "./ApprovalCheckList";
import DeleteDocumentModal from "./DeleteDocumentModal";
import DocumentActions from "./DocumentActions";
import DocumentTypeCol from "./DocumentTypeCol";
import DocumentTypeEditor from "./DocumentTypeEditor";
import ProviderReferralEditor from "./ProviderReferralEditor";
import ReplaceDocument from "./ReplaceDocument";
import ViewDocumentModal from "./ViewDocumentModal";

export default function DocumentsList({
  _case,
  documents,
  documentsCount,
  withPagination = false,
}) {
  const appointmentRepository = useService("repositories/appointment-repository");
  const providerRepository = useService("repositories/provider-repository");
  const hiddenDocumentTypeKeys = useHiddenDocumentTypeKeys();
  const { filters, updateFilter } = useQueryParams();
  const { revalidate } = useRevalidator();
  const { documentsOpts, patchDocument, documentValidation, providers } = useLoaderData();
  const [viewDocument, setViewDocument] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editingRows, setEditingRows] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const [selectedDocTypes, setSelectedDocTypes] = useState({});
  const [selectedProviders, setSelectedProviders] = useState({});
  const [selectedSources, setSelectedSources] = useState({});
  const [selectedReferralTo, setSelectedReferralTo] = useState({});
  const [selectedReferralFrom, setSelectedReferralFrom] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [errorArray, setErrorArray] = useState([]);
  const [fileReq, setFileReq] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [openAssociateAppointmentsModal, setOpenAssociateAppointmentsModal] = useState(false);
  const [isLaunchedFromAppointmentsTab, setIsLaunchedFromAppointmentsTab] = useState(false);
  const [docTypeChanged, setDocTypeChanged] = useState({});
  const { totalFolderCount, totalDocumentCount, setFolderId, parentFolderId } =
    useDocumentListStore();
  const [isDocSourceRequired, setIsDocSourceRequired] = useState(false);
  const [next, setNext] = useState(undefined);

  const fetchProvider = async (providerId) => {
    try {
      const provider = await providerRepository.findRecord(providerId);
      return provider || null;
    } catch (error) {
      console.error("Error fetching provider:", error);
      return null;
    }
  };

  const handleStartEditing = async (documentId) => {
    const doc = documents.find((d) => d.pk === documentId);

    setSelectedDocTypes((prev) => ({
      ...prev,
      [documentId]: documentsOpts.docTypeOptions.find((opt) => opt.value === doc.documentType),
    }));

    setSelectedSources((prev) => ({
      ...prev,
      [documentId]: documentsOpts.sourceOptions.find((opt) => opt.value === doc.source),
    }));

    if (doc.provider) {
      let providerObj = providers.find((p) => p.id === doc.provider);
      if (!providerObj) {
        providerObj = await fetchProvider(doc.provider);
      }
      if (providerObj) {
        setSelectedProviders((prev) => ({
          ...prev,
          [documentId]: providerObj,
        }));
      }
    }

    if (doc.referralTo) {
      let referralToObj = providers.find((p) => p.id === doc.referralTo);
      if (!referralToObj) {
        referralToObj = await fetchProvider(doc.referralTo);
      }
      if (referralToObj) {
        setSelectedReferralTo((prev) => ({
          ...prev,
          [documentId]: referralToObj,
        }));
      }
    }

    if (doc.referralFrom) {
      let referralFromObj = providers.find((p) => p.id === doc.referralFrom);
      if (!referralFromObj) {
        referralFromObj = await fetchProvider(doc.referralFrom);
      }
      if (referralFromObj) {
        setSelectedReferralFrom((prev) => ({
          ...prev,
          [documentId]: referralFromObj,
        }));
      }
    }

    setEditingRows((prev) => ({
      ...prev,
      [documentId]: true,
    }));
  };

  const handleCancelEditing = (documentId) => {
    setEditingRows((prev) => {
      const newState = { ...prev };
      delete newState[documentId];
      return newState;
    });

    setSelectedFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[documentId];
      return newFiles;
    });

    setSelectedDocTypes((prev) => {
      const newTypes = { ...prev };
      delete newTypes[documentId];
      return newTypes;
    });

    setSelectedProviders((prev) => {
      const newProviders = { ...prev };
      delete newProviders[documentId];
      return newProviders;
    });

    setSelectedSources((prev) => {
      const newSources = { ...prev };
      delete newSources[documentId];
      return newSources;
    });

    setSelectedReferralTo((prev) => {
      const newReferralTo = { ...prev };
      delete newReferralTo[documentId];
      return newReferralTo;
    });

    setSelectedReferralFrom((prev) => {
      const newReferralFrom = { ...prev };
      delete newReferralFrom[documentId];
      return newReferralFrom;
    });
  };

  const handleSaveEditing = async (documentId) => {
    setIsLoading(true);

    try {
      const formData = new FormData();
      if (selectedFiles[documentId]) {
        formData.append("file", selectedFiles[documentId]);
      }

      if (selectedDocTypes[documentId]) {
        formData.append("document_type", selectedDocTypes[documentId].value);
      }

      if (documentId in selectedProviders) {
        formData.append("provider", selectedProviders[documentId]?.id ?? "");
      }

      if (selectedSources[documentId]) {
        formData.append("source", selectedSources[documentId].value);
      }

      if (documentId in selectedReferralTo) {
        formData.append("referral_to", selectedReferralTo[documentId]?.id ?? "");
      }

      if (documentId in selectedReferralFrom) {
        formData.append("referral_from", selectedReferralFrom[documentId]?.id ?? "");
      }

      await patchDocument(documentId, formData);

      setUploadSuccess(true);
      handleCancelEditing(documentId);
    } catch (error) {
      setUploadFailed(true);
      setIsLoading(false);
      const errArr = ApiRequestErrorHandler(error.response?.data || error);
      setErrorArray(errArr);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (event, documentId) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [documentId]: event.target.files[0],
    }));
  };

  const handleDocumentTypeChange = (documentId, newType) => {
    setSelectedDocTypes((prev) => ({
      ...prev,
      [documentId]: newType,
    }));
    setDocTypeChanged((prev) => ({
      ...prev,
      [documentId]: true,
    }));
    if (newType && documentValidation?.validExtensions) {
      setFileReq(documentValidation.validExtensions[newType.value] || "");
    } else {
      setFileReq("");
    }
    if (documentValidation?.required?.source) {
      setIsDocSourceRequired(documentValidation.required.source.includes(newType?.value));
    }
    setSelectedProviders((prev) => ({
      ...prev,
      [documentId]: null,
    }));
    setSelectedReferralFrom((prev) => ({
      ...prev,
      [documentId]: null,
    }));
    setSelectedReferralTo((prev) => ({
      ...prev,
      [documentId]: null,
    }));
  };

  const handleSourceChange = (documentId, newSource) => {
    setSelectedSources((prev) => ({
      ...prev,
      [documentId]: newSource,
    }));
  };

  const handleReferralToChange = (documentId, newProvider) => {
    setSelectedReferralTo((prev) => ({
      ...prev,
      [documentId]: newProvider,
    }));
  };

  const handleReferralFromChange = (documentId, newProvider) => {
    setSelectedReferralFrom((prev) => ({
      ...prev,
      [documentId]: newProvider,
    }));
  };

  useEffect(() => {
    !openDeleteModal && setActiveDoc(null);
  }, [openDeleteModal]);

  useEffect(() => {
    updateFilter("limit", filters.get("limit") || 25);
  }, []);

  const handleUploadSuccess = () => {
    setUploadSuccess(false);
    const anyDocTypeChanged = Object.values(docTypeChanged).some((v) => v);
    if (anyDocTypeChanged && totalFolderCount === 0 && totalDocumentCount === 1) {
      setFolderId(parentFolderId);
    }
    setDocTypeChanged({});
    revalidate();
  };

  const handleProviderChange = (documentId, newProvider) => {
    setSelectedProviders((prev) => ({
      ...prev,
      [documentId]: newProvider,
    }));
  };

  const columns = [
    {
      field: "documentType",
      headerName: "Document Type",
      minWidth: 175,
      flex: 1.5,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <>
          <Box className="flex w-full">
            {editingRows[row.pk] && (
              <Box>
                {row.documentType !== undefined ? (
                  <Tooltip title={`Current Type: ${row.fileName}`}>
                    <Info style={{ color: "green" }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Document is missing type">
                    <Error style={{ color: "red" }} />
                  </Tooltip>
                )}
              </Box>
            )}
            <DocumentTypeCol
              document={row}
              docTypeOptions={documentsOpts.docTypeOptions}
              handleOpenDocument={handleOpenDocument}
            />
          </Box>
        </>
      ),
    },
    {
      field: "createdAt",
      headerName: "Time Stamps",
      flex: 2,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) =>
        editingRows[row.pk] ? (
          <ReplaceDocument
            selectedFileName={
              selectedFiles[row.pk]?.name
                ? selectedFiles[row.pk].name.substring(0, 15) + "..."
                : null
            }
            handleFileSelect={handleFileSelect}
            rowId={row.pk}
          />
        ) : (
          <>
            <Box className="flex flex-wrap items-center justify-between">
              {row.createdAt && (
                <p className="w-full">
                  <span className="font-bold">Created:</span>{" "}
                  {FormatISODate(row.createdAt, "MM/dd/yyyy")}
                </p>
              )}
              {row.updatedAt && (
                <p className="w-full">
                  <span className="font-bold">Updated:</span>{" "}
                  {FormatISODate(row.updatedAt, "MM/dd/yyyy")}
                </p>
              )}
            </Box>
          </>
        ),
    },
    {
      field: "createdByName",
      headerName: "Updated By",
      flex: 2,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        if (editingRows[row.pk]) {
          return (
            <DocumentTypeEditor
              documentType={row.documentType}
              docTypeOptions={getSelectableDocTypes(
                documentsOpts.docTypeOptions,
                hiddenDocumentTypeKeys || [],
                row.documentType
              )}
              selectedDocType={selectedDocTypes[row.pk]}
              handleDocumentTypeChange={handleDocumentTypeChange}
              rowId={row.pk}
              fileReq={fileReq}
            />
          );
        }
        return (
          <Box className="flex flex-wrap items-center justify-between">
            {
              <p className="w-full">
                <span className="font-bold">Created by:</span>{" "}
                {!row.createdByName ? "Unknown" : row.createdByName}
              </p>
            }
            {
              <p className="w-full">
                <span className="font-bold">Updated by:</span>{" "}
                {!row.updatedByName ? "Unknown" : row.updatedByName}
              </p>
            }
          </Box>
        );
      },
    },
    {
      field: "provider",
      headerName: "Provider/Referral",
      flex: 1.5,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      colSpan: (row) => {
        return editingRows[row?.pk] ? 2 : 1;
      },
      renderCell: ({ row }) => {
        const currentSelectedDocType = selectedDocTypes[row.pk];
        const isEditing = !!editingRows[row.pk];
        const docTypeValue = isEditing ? currentSelectedDocType?.value : row.documentType;
        const showProvider = documentValidation?.required?.provider?.includes(docTypeValue);
        const showReferralTo = documentValidation?.required?.referral_to?.includes(docTypeValue);
        const showReferralFrom =
          documentValidation?.required?.referral_from?.includes(docTypeValue);
        const showReferralNote = showReferralTo || showReferralFrom;

        if (isEditing) {
          return (
            <ProviderReferralEditor
              showProvider={showProvider}
              showReferralTo={showReferralTo}
              showReferralFrom={showReferralFrom}
              showReferralNote={showReferralNote}
              providers={providers}
              selectedProvider={selectedProviders[row.pk]}
              selectedReferralTo={selectedReferralTo[row.pk]}
              selectedReferralFrom={selectedReferralFrom[row.pk]}
              handleProviderChange={handleProviderChange}
              handleReferralToChange={handleReferralToChange}
              handleReferralFromChange={handleReferralFromChange}
              rowId={row.pk}
            />
          );
        }

        return (
          <Box>
            {row.providerName && showProvider ? (
              row.providerName
            ) : (row.referralFromName && showReferralFrom) ||
              (row.referralToName && showReferralTo) ? (
              <>
                <strong>To:</strong> {row?.referralToName || STRINGS.NA}
                <br />
                <strong>From:</strong> {row?.referralFromName || STRINGS.NA}
              </>
            ) : (
              ""
            )}
          </Box>
        );
      },
    },
    {
      field: "associatedAppointmentCount",
      headerName: "Related Appointments",
      flex: 1,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        if (editingRows[row.pk]) {
          return null;
        }
        return `${Number(row?.associatedAppointmentCount ?? 0)}/${Number(row?.providerTotalAppointmentCount ?? 0)}`;
      },
    },
    {
      field: "source",
      headerName: "Source",
      flex: 1.5,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: ({ value, row }) => {
        if (editingRows[row.pk]) {
          return (
            <Box className="w-full">
              <Autocomplete
                size="small"
                className="flex w-full items-center"
                options={documentsOpts.sourceOptions || []}
                getOptionLabel={(option) => option.display_name}
                value={
                  selectedSources[row.pk] ||
                  documentsOpts.sourceOptions.find((option) => option.value === value) ||
                  null
                }
                onChange={(e, value) => handleSourceChange(row.pk, value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={"Source" + (isDocSourceRequired ? "*" : "")}
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </Box>
          );
        }
        return (
          documentsOpts.sourceOptions.find((source) => source.value === value)?.display_name ?? ""
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      align: "right",
      width: 433,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        const deleteButton = (
          <CustomIconButton
            tooltipText="Delete"
            handleOnClick={() => handleDeleteDocument(row)}
            iconVisible
            CustomIcon={<Delete className="text-red-500" />}
          />
        );

        if (editingRows[row.pk]) {
          return (
            <Box className="flex items-center">
              <ModalButton
                action={() => handleSaveEditing(row.pk)}
                baseColor="#16A34A"
                icon="add"
                content={"Submit"}
              />
              {deleteButton}
              <CustomIconButton
                tooltipText="Cancel edit"
                handleOnClick={() => handleCancelEditing(row.pk)}
                iconVisible
                CustomIcon={<Close style={{ color: "red" }} />}
              />
            </Box>
          );
        }

        return (
          <Box className="flex justify-between px-4">
            <DocumentActions
              document={row}
              handleOpenAssociateAppointmentModal={handleOpenAssociateAppointmentModal}
              setIsLaunchedFromAppointmentsTab={setIsLaunchedFromAppointmentsTab}
            />
            <ApprovalChecklist document={row} />
            {deleteButton}
            <CustomIconButton
              tooltipText="Edit"
              handleOnClick={() => handleStartEditing(row.pk)}
              iconVisible
              CustomIcon={<Edit className="text-gray-500" />}
            />
          </Box>
        );
      },
    },
  ];

  function getRowId(row) {
    return row.pk;
  }

  function handleOpenDocument(document) {
    setViewDocument(true);
    setActiveDoc(document);
  }

  function closeDocModal() {
    setViewDocument(false);
    setActiveDoc(null);
    setNext(undefined);
  }

  function handleDeleteDocument(doc) {
    setOpenDeleteModal(true);
    setActiveDoc(doc);
  }

  const handleOpenAssociateAppointmentModal = async (doc, shouldOpen, nextPageUrl = null) => {
    setActiveDoc(doc);
    try {
      if (doc) {
        const queryParams = {
          ordering: "-appointment_date, -appointment_time",
          include_administered: true,
          provider: doc.provider,
          client: _case.client.id,
        };
        const params = new URLSearchParams(nextPageUrl);
        queryParams.limit = params.get("limit");
        queryParams.offset = params.get("offset");

        const appt = await appointmentRepository.query(queryParams);
        const nextPage = await appointmentRepository.getNextPage();
        setNext(nextPage);
        if (appt) {
          setAppointments(appt);
          setOpenAssociateAppointmentsModal(shouldOpen);
        }
      }
    } catch (error) {
      const errArr = ApiRequestErrorHandler(error ?? []);
      setErrorArray(errArr);
    }
  };

  const handleCloseAssociateAppointmentModal = () => {
    setOpenAssociateAppointmentsModal(false);
  };

  return (
    <>
      <Box className="h-[calc(100vh-12rem)] w-full overflow-y-auto p-4">
        <CustomDataGrid
          columns={columns}
          getRowId={getRowId}
          disableRowSelectionOnClick
          disableMultipleRowSelection
          getRowHeight={(params) => (editingRows[params.pk] ? 160 : "auto")}
          getEstimatedRowHeight={() => 100}
          rowSelection={false}
          rows={documents ?? []}
          sx={{
            "& .MuiDataGrid-cell": {
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              padding: "1rem 0",
              borderBottom: `1px solid ${Colors.gray[100]}`,
              overflow: "visible",
              boxSizing: "border-box",
            },
          }}
        />
        {withPagination && documentsCount && (
          <Pagination filters={filters} updateFilter={updateFilter} totalCount={documentsCount} />
        )}
      </Box>
      {activeDoc && viewDocument && (
        <ViewDocumentModal
          viewDocument={viewDocument}
          closeDocModal={closeDocModal}
          activeDoc={activeDoc}
        />
      )}
      {activeDoc && openDeleteModal && (
        <DeleteDocumentModal
          doc={activeDoc}
          openDeleteModal={openDeleteModal}
          setOpenDeleteModal={setOpenDeleteModal}
          getExtension={getExtension}
          documentTypeObj={documentsOpts.docTypeOptions}
        />
      )}
      {appointments && appointments.length > 0 && (
        <ModalShell
          open={openAssociateAppointmentsModal}
          setOpen={handleCloseAssociateAppointmentModal}
          fullWidth
          customWidth="xl"
        >
          <AssociateAppointments
            document={activeDoc}
            setOpenAssociateAppointmentsModal={handleCloseAssociateAppointmentModal}
            appointments={appointments}
            isLaunchedFromAppointmentsTab={isLaunchedFromAppointmentsTab}
            selectedAppointment={null}
            nextPageUrl={next}
            handleOpenAssociateAppointmentModal={handleOpenAssociateAppointmentModal}
          />
        </ModalShell>
      )}
      <LoadingIndicator isLoading={isLoading} />
      <ResponseModal
        title="Update Success"
        description="The image/document was successfully updated"
        openBool={uploadSuccess}
        setOpenBool={setUploadSuccess}
        handleCloseFunc={handleUploadSuccess}
      />
      <ResponseModal
        title="Update failed"
        isError={true}
        openBool={uploadFailed}
        setOpenBool={setUploadFailed}
        errorMessage={errorArray}
      />
    </>
  );
}
